import { Box, Button, GlobalStyles, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import React, { useCallback, useState } from 'react';

import {
  buildFeedbackContext,
  readFeedbackEnvironment,
} from './feedback-context';
import FeedbackDialog from './feedback-dialog';
import { captureViewport, ViewportCapture } from './feedback-screenshot';
import { FeedbackTarget } from './feedback-target';
import { useRegionPicker } from './use-region-picker';

import { useSnackbarContext } from '~/client/components/base/snackbar';
import { useFeedbackMode } from '~/client/contexts/feedback-mode-context';
import { useUserProfile } from '~/client/hooks/user-profile';
import * as GQL from '~/graphql/generated/client';
import type { FeedbackContextValue } from '~/shared/utils/feedback-input';

interface Draft {
  context: FeedbackContextValue;
  screenshot: string | null;
  thumbnail: string | null;
}

const LAYER_Z_INDEX = 1350;

function issueNumber(url: string | null | undefined): string | null {
  const match = url?.match(/\/issues\/(\d+)/);
  return match ? match[1] : null;
}

const FeedbackPicker: React.FC = () => {
  const theme = useTheme();
  const Snackbar = useSnackbarContext();
  const { setActive } = useFeedbackMode();
  const { userProfile } = useUserProfile();

  const [capturing, setCapturing] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [submitFeedback, { loading: submitting }] =
    GQL.useSubmitFeedbackMutation();

  const picking = draft == null && !capturing;

  const pick = useCallback(async (target: FeedbackTarget) => {
    setCapturing(true);
    const rect = target.element.getBoundingClientRect();
    const context = buildFeedbackContext(target, readFeedbackEnvironment());
    let shots: ViewportCapture = { screenshot: null, thumbnail: null };
    try {
      shots = await captureViewport(rect);
    } catch (err) {
      console.warn('Feedback screenshot could not be captured', err);
    }
    setSubmitError(null);
    setDraft({ context, ...shots });
    setCapturing(false);
  }, []);

  const { highlight, clearHighlight } = useRegionPicker({
    enabled: picking,
    onPick: (target) => {
      pick(target)
        .catch(() => undefined)
        .finally(clearHighlight);
    },
    onExit: () => setActive(false),
  });

  const handleSubmit = async (values: {
    comment: string;
    expected: string;
  }) => {
    if (!draft) {
      return;
    }
    setSubmitError(null);
    try {
      const result = await submitFeedback({
        variables: {
          input: {
            comment: values.comment,
            expected: values.expected || null,
            screenshot: draft.screenshot,
            thumbnail: draft.thumbnail,
            context: draft.context,
          },
        },
      });
      const number = issueNumber(result.data?.submitFeedback.githubIssueUrl);
      setDraft(null);
      Snackbar.open(
        number
          ? `Thanks, your feedback was filed as issue #${number}.`
          : 'Thanks, your feedback was sent.',
        { showOk: true }
      );
    } catch (err) {
      setSubmitError(
        err instanceof Error && err.message
          ? err.message
          : 'Your feedback could not be sent. Please try again.'
      );
    }
  };

  const reporterLabel =
    [userProfile.email, userProfile.currentOrganizationName]
      .filter(Boolean)
      .join(' · ') || null;

  return (
    <>
      {picking && (
        <GlobalStyles
          styles={{
            'body, body *': { cursor: 'crosshair !important' },
            '[data-feedback-ui], [data-feedback-ui] *': {
              cursor: 'auto !important',
            },
            '[data-feedback-ui] button': { cursor: 'pointer !important' },
          }}
        />
      )}

      {draft == null && (
        <Box
          data-feedback-ui="banner"
          role="status"
          sx={{
            position: 'fixed',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: LAYER_Z_INDEX + 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 0.75,
            borderRadius: 999,
            bgcolor: alpha(theme.palette.grey[900], 0.88),
            color: theme.palette.common.white,
            boxShadow: theme.shadows[4],
            maxWidth: 'calc(100vw - 24px)',
            '@media print': { display: 'none' },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {capturing
              ? 'Capturing screenshot…'
              : 'Feedback mode: click any area to comment, Esc to exit'}
          </Typography>
          <Button
            size="small"
            onClick={() => setActive(false)}
            sx={{ color: 'inherit', minWidth: 0, textTransform: 'none' }}
          >
            Exit
          </Button>
        </Box>
      )}

      {highlight && draft == null && (
        <Box
          data-feedback-ui="highlight"
          aria-hidden
          sx={{
            position: 'fixed',
            top: highlight.top,
            left: highlight.left,
            width: highlight.width,
            height: highlight.height,
            zIndex: LAYER_Z_INDEX,
            pointerEvents: 'none',
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '-1px',
            borderRadius: '4px',
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            transition:
              'top 120ms ease, left 120ms ease, width 120ms ease, height 120ms ease',
          }}
        >
          <Box
            component="span"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              transform: 'translateY(-100%)',
              px: 0.75,
              py: 0.25,
              fontSize: 11,
              lineHeight: 1.4,
              borderRadius: '4px 4px 0 0',
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              maxWidth: 320,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {highlight.label}
          </Box>
        </Box>
      )}

      <FeedbackDialog
        open={draft != null}
        screenshot={draft?.screenshot ?? null}
        context={draft?.context ?? null}
        reporterLabel={reporterLabel}
        submitting={submitting}
        error={submitError}
        onCancel={() => {
          setDraft(null);
          setSubmitError(null);
        }}
        onSubmit={handleSubmit}
      />
    </>
  );
};

const AuthenticatedFeedbackPicker: React.FC = () => {
  const { isAuthenticated } = useUserProfile();
  if (!isAuthenticated) {
    return null;
  }
  return <FeedbackPicker />;
};

export const FeedbackLayer: React.FC = () => {
  const { active } = useFeedbackMode();
  if (!active) {
    return null;
  }
  return <AuthenticatedFeedbackPicker />;
};

export default FeedbackLayer;
